/* eslint-disable eqeqeq */
import { denormalize, normalize, schema } from 'normalizr';
import * as R from 'ramda';
import {
  AUTOMATIC_RESPONSE_TYPES,
  INSPECTION_CHECKBOX_CONDITIONS,
  INSPECTION_FORM_ACTION_TYPES,
  INSPECTION_ITEM_RESPONSE,
  INSPECTION_ITEM_TYPE,
  INSPECTION_LIST_CONDITIONS,
  INSPECTION_NUMBER_CONDITIONS,
} from '@/_legacy/constants';

const listConditionIsMet = (condition, _responded, response) => {
  let conditionArray;
  if (condition.value && condition.value.constructor === Array) {
    conditionArray = condition.value;
    conditionArray.sort();
  } else {
    conditionArray = [condition.value];
  }

  const responseArray = Array.isArray(response) ? response : [response].filter(v => v);
  responseArray.sort();

  return {
    [INSPECTION_LIST_CONDITIONS.IS]: () => R.symmetricDifference(conditionArray, responseArray).length === 0,
    [INSPECTION_LIST_CONDITIONS.IS_NOT]: () => R.symmetricDifference(conditionArray, responseArray).length > 0,
    [INSPECTION_LIST_CONDITIONS.EMPTY]: () => responseArray.length === 0,
    [INSPECTION_LIST_CONDITIONS.NOT_EMPTY]: () => responseArray.length > 0,
    [INSPECTION_LIST_CONDITIONS.ONE_OF]: () => responseArray.length > 0
      && responseArray.some(r => conditionArray.includes(r)),
    [INSPECTION_LIST_CONDITIONS.NOT_ONE_OF]: () => responseArray.length > 0
      && !responseArray.some(r => conditionArray.includes(r)),
    [INSPECTION_LIST_CONDITIONS.INCLUDES]: () => responseArray.length > 0
      && R.intersection(responseArray, conditionArray).length
      === conditionArray.length,
  }[condition.operator]?.() || false;
};

const entityItemsConditionIsMet = (condition, _responded, response) => {
  let conditionArray;
  if (condition.value && condition.value.constructor === Array) {
    conditionArray = condition.value;
    conditionArray.sort();
  } else {
    conditionArray = [condition.value];
  }
  conditionArray = conditionArray.map(item => parseInt(item));
  const responseArray = Array.isArray(response) ? response : [response].filter(v => v);
  responseArray.sort();

  return {
    [INSPECTION_LIST_CONDITIONS.IS]: () => R.symmetricDifference(conditionArray, responseArray).length === 0,
    [INSPECTION_LIST_CONDITIONS.IS_NOT]: () => R.symmetricDifference(conditionArray, responseArray).length > 0,
    [INSPECTION_LIST_CONDITIONS.EMPTY]: () => responseArray.length === 0,
    [INSPECTION_LIST_CONDITIONS.NOT_EMPTY]: () => responseArray.length > 0,
    [INSPECTION_LIST_CONDITIONS.ONE_OF]: () => responseArray.length > 0
      && responseArray.some(r => conditionArray.includes(r)),
    [INSPECTION_LIST_CONDITIONS.NOT_ONE_OF]: () => responseArray.length > 0
      && !responseArray.some(r => conditionArray.includes(r)),
    [INSPECTION_LIST_CONDITIONS.INCLUDES]: () => responseArray.length > 0
      && R.intersection(responseArray, conditionArray).length
      === conditionArray.length,
  }[condition.operator]?.() || false;
};

export const parseNumber = value => Number(value.toString().replace(/,/, '.'));

const numberConditionIsMet = (condition, responded, response) => {
  // unfortuanetly Number("") is 0 for some reason
  // we have to check for that
  if (!responded || response.trim() === '') {
    return false;
  }

  const parsedResponse = parseNumber(response);

  if (condition.operator === INSPECTION_NUMBER_CONDITIONS.BETWEEN) {
    const [min, max] = condition.value || [];
    const parsedMin = parseNumber(min);
    const parsedMax = parseNumber(max);

    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax)) {
      return false;
    }

    return parsedResponse >= parsedMin && parsedResponse <= parsedMax;
  }

  const parsedConditionValue = parseNumber(condition.value);

  if (Number.isNaN(parsedResponse)) {
    return false;
  }

  return {
    [INSPECTION_NUMBER_CONDITIONS.LESS]: () => parsedResponse < parsedConditionValue,
    [INSPECTION_NUMBER_CONDITIONS.LESS_EQUAL]: () => parsedResponse <= parsedConditionValue,
    [INSPECTION_NUMBER_CONDITIONS.EQUAL]: () => parsedResponse === parsedConditionValue,
    [INSPECTION_NUMBER_CONDITIONS.NOT_EQUAL]: () => parsedResponse !== parsedConditionValue,
    [INSPECTION_NUMBER_CONDITIONS.GREATER]: () => parsedResponse > parsedConditionValue,
    [INSPECTION_NUMBER_CONDITIONS.GREATER_EQUAL]: () => parsedResponse >= parsedConditionValue,
  }[condition.operator]?.() || false;
};

const checkboxConditionIsMet = (condition, _responded, response) => ({
  [INSPECTION_CHECKBOX_CONDITIONS.IS_TRUE]: () => response === true,
  [INSPECTION_CHECKBOX_CONDITIONS.IS_FALSE]: () => response === false,
}[condition.operator]?.() || false);

export const textConditionIsMet = (condition, _responded, response) => {
  const normalizedResponse = (response || '').toLowerCase().trim();
  const normalizedValue = (condition.value || '').toLowerCase().trim();

  switch (condition.operator) {
    case 'is':
      return normalizedResponse === normalizedValue;
    case 'is_not':
      return Boolean(normalizedResponse) && normalizedResponse !== normalizedValue;
    case 'includes':
      return Boolean(normalizedResponse) && normalizedResponse.includes(normalizedValue);
    case 'excludes':
      return normalizedResponse && !normalizedResponse.includes(normalizedValue);
    case 'empty':
      return !normalizedResponse;
    case 'not_empty':
      return Boolean(normalizedResponse);
    default:
      return false;
  }
};

const itemSchema = new schema.Entity('items', {}, { idAttribute: 'uuid' });
itemSchema.define({ items: [itemSchema] });

const pageSchema = new schema.Entity(
  'pages',
  { items: [itemSchema] },
  { idAttribute: 'uuid' },
);

const responseSetSchema = new schema.Entity(
  'response_sets',
  {},
  { idAttribute: 'uuid' },
);

const formSchema = new schema.Entity(
  'form',
  {
    items: [pageSchema],
    params: { response_sets: [responseSetSchema] },
  },
  { idAttribute: 'uuid' },
);

const inspectionSchema = new schema.Entity('inspection', { form: formSchema });

export const checkIfAllConditionsAreMet = (conditionalSection, item) => {
  if (
    ![
      INSPECTION_ITEM_RESPONSE.ENTITY_ITEM,
      INSPECTION_ITEM_RESPONSE.LIST,
      INSPECTION_ITEM_RESPONSE.NUMBER,
      INSPECTION_ITEM_RESPONSE.CHECKBOX,
      INSPECTION_ITEM_RESPONSE.TEXT,
    ].includes(item.response_type)
  ) {
    return true;
  }

  const checkFunction = {
    [INSPECTION_ITEM_RESPONSE.ENTITY_ITEM]: entityItemsConditionIsMet,
    [INSPECTION_ITEM_RESPONSE.LIST]: listConditionIsMet,
    [INSPECTION_ITEM_RESPONSE.NUMBER]: numberConditionIsMet,
    [INSPECTION_ITEM_RESPONSE.CHECKBOX]: checkboxConditionIsMet,
    [INSPECTION_ITEM_RESPONSE.TEXT]: textConditionIsMet,
  }[item.response_type];

  if (!conditionalSection.conditions) {
    return false;
  }

  return conditionalSection.conditions.reduce((acc, condition) => {
    if (!acc) {
      return false;
    }

    const response = item.response_type === INSPECTION_ITEM_RESPONSE.TEXT && item.rte_response
      ? getTextFromHtmlResponse(item.html_response)
      : item.response;

    return checkFunction(condition, item.responded, response);
  }, true);
};

const getTextFromHtmlResponse = response => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = response;

  return tempDiv.textContent;
};

export const findHideFormAction = conditionalSection => {
  if (!conditionalSection?.items) {
    return null;
  }

  return conditionalSection
    .items
    .find(
      action => action.type === INSPECTION_ITEM_TYPE.FORM_ACTION && action.action === INSPECTION_FORM_ACTION_TYPES.HIDE
    ) || null;
};

export const hasHideFormAction = conditionalSection => {
  return !!findHideFormAction(conditionalSection);
};

export const findItem = (item, uuid) => {
  if (item.uuid === uuid) {
    return item;
  }

  if (item.items) {
    for (const i in item.items) {
      const res = findItem(item.items[i], uuid);
      if (res !== false) {
        return res;
      }
    }
  }

  return false;
};

export const findItemIsInRepeatedSection = (item, uuid, repeated = false) => {
  if (item.uuid === uuid) {
    return repeated;
  }

  if (item.items) {
    for (const i in item.items) {
      const isRepeated = repeated || item.items[i].repeat;
      const res = findItemIsInRepeatedSection(item.items[i], uuid, isRepeated);
      if (res !== false) {
        return res;
      }
    }
  }

  return false;
};

export const normalizeInspection = inspection => normalize(inspection, inspectionSchema);
export const denormalizeInspection = (result, entities) => denormalize(result, inspectionSchema, entities);
export const validateInspectionItem = (item, $t) => {
  const isRequired = item.required && !item.responded;
  const isNumber = item.response_type === INSPECTION_ITEM_RESPONSE.NUMBER
    && item.responded
    && Number.isNaN(parseNumber(item.response));

  const errors = [];
  if (isRequired) {
    errors.push($t('This question is required.'));
  }
  if (isNumber) {
    errors.push(
      $t(
        'Response must be a number. At most one decimal separator, and no thousand separator.',
      ),
    );
  }

  return errors;
};
export const validateInspection = (entities, { $t } = {}) => {
  const items = Object.values(entities.items) || [];

  const questionsFromUnmetConditionals = items
    .filter(
      ({ type, allConditionsMet }) => type === INSPECTION_ITEM_TYPE.CONDITIONAL && !allConditionsMet,
    )
    .reduce((acc, value) => [...acc, ...value.items], []);

  const filteredItems = items.filter(({ uuid, type, repeat }) => {
    if (
      type !== INSPECTION_ITEM_TYPE.QUESTION
      || questionsFromUnmetConditionals.includes(uuid)
      || repeat
    ) {
      return false;
    }
    return true;
  });

  return filteredItems.reduce((acc, item) => {
    const errors = validateInspectionItem(item, $t);
    if (errors.length <= 0) {
      return acc;
    }

    return {
      ...acc,
      [item.uuid]: errors,
    };
  }, {});
};

export const canHaveChildrenHiddenForFormHideAction = item => {
  return [
    INSPECTION_ITEM_TYPE.SECTION,
    INSPECTION_ITEM_TYPE.PAGE,
    INSPECTION_ITEM_TYPE.FORM,
    INSPECTION_ITEM_TYPE.ACTION,
    INSPECTION_ITEM_TYPE.INSPECTION,
    INSPECTION_ITEM_TYPE.CONDITIONAL,
  ].includes(item.type);
};

export const isAllowedToBeHiddenAfterAction = item => {
  return [
    INSPECTION_ITEM_TYPE.SECTION,
    INSPECTION_ITEM_TYPE.QUESTION,
    INSPECTION_ITEM_TYPE.ACTION,
    INSPECTION_ITEM_TYPE.INSPECTION,
    INSPECTION_ITEM_TYPE.CONDITIONAL,
  ].includes(item.type);
};

export const isHiddenAfterFormAction = item => {
  return item.hideAfterAction;
};

const isAssigneesValid = item => {
  const users = item.params.assignees?.users || [];
  const groups = item.params.assignees?.groups || [];
  const associateAssignees = item.params.associateAssignees || [];
  const allAssignees = [...users, ...groups, ...associateAssignees];

  return item.settings.assignees_required && allAssignees && allAssignees.length < 1;
};

const isDescriptionValid = item => item.settings.description_required
  && !item.params.description;

const isDueAfterValid = item => item.settings.due_after_required
  && !item.params.due_in;

const isPriorityValid = item => item.settings.priority_required
  && !item.params.priority;

const isTitleValid = item => item.settings.title_required
  && !item.params.title;

const getInvalidAction = items => {
  let invalidElement = null;

  const isInvalid = item => {
    // Old actions doesn't have `issues` so we need to
    // check that we are not dealing with old issues
    if (!Object.prototype.hasOwnProperty.call(item, 'settings')) {
      return false;
    }

    if (isHiddenAfterFormAction(item)) {
      return false;
    }

    if (isAssigneesValid(item)) {
      return true;
    }

    if (isDescriptionValid(item)) {
      return true;
    }

    if (isDueAfterValid(item)) {
      return true;
    }

    if (isPriorityValid(item)) {
      return true;
    }

    if (isTitleValid(item)) {
      return true;
    }

    return false;
  };

  const findFormItem = (arr, _item, parent = null) => {
    arr.forEach(element => {
      if (element.type === 'action' && element.action === 'issue' && isInvalid(element)) {
        if (parent.allConditionsMet) {
          invalidElement = element;
        }
      }

      if (element.items) {
        findFormItem(element.items, _item, element);
      }
    });
  };

  findFormItem(items);

  return invalidElement;
};

export const isAutomaticResponse = item => [
  AUTOMATIC_RESPONSE_TYPES.USER_AUTOMATIC,
  AUTOMATIC_RESPONSE_TYPES.DATE_TIME_AUTOMATIC,
  AUTOMATIC_RESPONSE_TYPES.SITE_AUTOMATIC,
].includes(item.response_type);

export default {
  checkIfAllConditionsAreMet,
  normalizeInspection,
  denormalizeInspection,
  validateInspectionItem,
  validateInspection,
  getInvalidAction,
  isAutomaticResponse,
  parseNumber,
  textConditionIsMet,
};
